import {inject, injectable} from 'inversify'
import {DI} from '@/src/config/di/types'
import type {FavouriteRepositoryInterface} from './repository.interface'
import type {FavouriteProductId} from '@/src/features/favourite/core/types'
import {FavouriteError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import type {IErrorReporter} from '@/src/ports/services/monitoring'

export type ToggleFavouriteResult = {
  productId: Id
  isNowFavourite: boolean
  updatedIds: FavouriteProductId[]
}

/**
 * Toggle Favourite Use Case
 *
 * Orchestrates adding/removing a product from favourites.
 * Handles the toggle logic and persistence.
 *
 * @example
 * const result = await toggleFavouriteUseCase.execute(productId, currentFavourites)
 * if (result.isOk()) {
 *   // Update UI with new favourite state
 * }
 */
@injectable()
export class ToggleFavouriteUseCase {
  @inject(DI.FavouriteRepositoryInterface)
  repository!: FavouriteRepositoryInterface

  @inject(DI.IErrorReporter)
  errorReporter!: IErrorReporter

  async execute(
    productId: Id,
    currentFavouriteIds: FavouriteProductId[],
  ): Promise<OperationResult<ToggleFavouriteResult, FavouriteError>> {
    const isCurrentlyFavourite = currentFavouriteIds.includes(productId)

    let updatedIds: FavouriteProductId[]

    if (isCurrentlyFavourite) {
      updatedIds = currentFavouriteIds.filter(id => id !== productId)
    } else {
      updatedIds = [...currentFavouriteIds, productId]
    }

    try {
      await this.repository.set(updatedIds)

      return Result.ok({
        productId,
        isNowFavourite: !isCurrentlyFavourite,
        updatedIds,
      })
    } catch (error) {
      this.errorReporter.capture(
        error instanceof Error ? error : new Error(String(error)),
        {product_id: productId},
      )
      return Result.fail(new FavouriteError('TOGGLE_FAILED', error))
    }
  }
}
